/**
 * Isometric line drawings for the SEO process (Analys, Strategi, Genomförande,
 * Uppföljning). All four share one projection, viewBox, stroke weight and scale;
 * the helpers are exported so other service pages (webbyrå) draw in the same style.
 *
 * Every drawing is split into groups the section animates separately:
 * - `.cc-ill__layer`  solid outlines (base, always visible) + a `.cc-ill__draw`
 *                      copy with pathLength=1 for the stroke-dashoffset draw-on
 * - `.cc-ill__construct` dashed construction lines (revealed by a mask)
 * - `.cc-ill__crosses` construction crosses (shown on hover)
 * - `.cc-ill__accent`  teal details with the step-specific animation
 */

import type { CSSProperties, ReactNode } from "react";

export const ILL_VIEWBOX = "0 0 260 190";

const ORIGIN_X = 130;
const ORIGIN_Y = 64;
const COS30 = 0.866;
/** Shared scale so every drawing fills the frame the same way. */
const SCALE = 1.14;

export type Point3 = readonly [number, number, number];

/** Isometric projection: x runs right-down, y left-down, z up. */
export function project([x, y, z]: Point3): [number, number] {
  return [ORIGIN_X + (x - y) * COS30 * SCALE, ORIGIN_Y + ((x + y) * 0.5 - z) * SCALE];
}

function fmt([x, y]: [number, number]) {
  return `${x.toFixed(1)} ${y.toFixed(1)}`;
}

export function polyline(points: Point3[], close = false) {
  const d = points.map((point, index) => `${index ? "L" : "M"}${fmt(project(point))}`).join(" ");
  return close ? `${d} Z` : d;
}

export function plane(x: number, y: number, z: number, w: number, d: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y + d, z],
      [x, y + d, z],
    ],
    true,
  );
}

export type Shape = { d: string; fill?: boolean };

/** Visible faces of a box (top, front-left, front-right), filled to hide what's behind. */
export function box(x: number, y: number, z: number, w: number, d: number, h: number): Shape[] {
  return [
    {
      d: polyline(
        [
          [x, y + d, z],
          [x + w, y + d, z],
          [x + w, y + d, z + h],
          [x, y + d, z + h],
        ],
        true,
      ),
      fill: true,
    },
    {
      d: polyline(
        [
          [x + w, y, z],
          [x + w, y + d, z],
          [x + w, y + d, z + h],
          [x + w, y, z + h],
        ],
        true,
      ),
      fill: true,
    },
    { d: plane(x, y, z + h, w, d), fill: true },
  ];
}

export function line(from: Point3, to: Point3): Shape {
  return { d: polyline([from, to]) };
}

/** A path relative to its own last point, so an animateMotion ends at (0, 0). */
export function motionPath(points: Point3[]) {
  const projected = points.map(project);
  const [endX, endY] = projected[projected.length - 1];
  return projected
    .map(([x, y], index) => `${index ? "L" : "M"}${(x - endX).toFixed(1)} ${(y - endY).toFixed(1)}`)
    .join(" ");
}

function Cross({ at }: { at: [number, number] }) {
  const [x, y] = at;
  return <path d={`M${x - 5} ${y}h10M${x} ${y - 5}v10`} />;
}

function Layer({ shapes, className = "" }: { shapes: Shape[]; className?: string }) {
  return (
    <g className={`cc-ill__layer ${className}`.trim()}>
      <g className="cc-ill__base">
        {shapes.map((shape, index) => (
          <path key={index} d={shape.d} className={shape.fill ? "cc-ill__face" : undefined} />
        ))}
      </g>
      <g className="cc-ill__draw">
        {shapes.map((shape, index) => (
          <path key={index} d={shape.d} pathLength={1} />
        ))}
      </g>
    </g>
  );
}

type IllustrationFrameProps = {
  layers: { className?: string; shapes: Shape[] }[];
  construct: string[];
  crosses: [number, number][];
  maskId: string | null;
  children: ReactNode;
};

export function IllustrationFrame({ layers, construct, crosses, maskId, children }: IllustrationFrameProps) {
  return (
    <svg className="cc-ill" viewBox={ILL_VIEWBOX} aria-hidden="true" focusable="false">
      {maskId ? (
        <defs>
          {/* Reveals the dashed lines without changing their dash pattern. */}
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="260" height="190">
            <rect className="cc-ill__mask-rect" x="0" y="0" width="260" height="190" fill="#fff" />
          </mask>
        </defs>
      ) : null}
      <g className="cc-ill__construct" mask={maskId ? `url(#${maskId})` : undefined}>
        {construct.map((d, index) => (
          <path key={index} d={d} />
        ))}
      </g>
      <g className="cc-ill__crosses">
        {crosses.map((at, index) => (
          <Cross key={index} at={at} />
        ))}
      </g>
      {layers.map((layer, index) => (
        <Layer key={index} shapes={layer.shapes} className={layer.className} />
      ))}
      <g className="cc-ill__accent">{children}</g>
    </svg>
  );
}

export const CORNER_CROSSES: [number, number][] = [
  [16, 14],
  [244, 14],
  [16, 176],
  [244, 176],
];

export function floorGrid(size: number, step: number, z = 0) {
  const lines: string[] = [];
  for (let value = step; value < size; value += step) {
    lines.push(polyline([[value, 0, z], [value, size, z]]));
    lines.push(polyline([[0, value, z], [size, value, z]]));
  }
  lines.push(plane(0, 0, z, size, size));
  return lines;
}

// ---------------------------------------------------------------- 01 Analys

const ANALYSIS_HOME: Point3 = [50, 50, 54];
const ANALYSIS_CHILDREN: Point3[] = [
  [16, 16, 24],
  [84, 16, 24],
  [16, 84, 24],
  [84, 84, 24],
];

export function CcSeoAnalysisIllustration({ maskId }: { maskId: string | null }) {
  const sheet = (x: number, y: number, z: number, size: number) => box(x, y, z, size, size, 2);
  const shapes: Shape[] = [
    ...sheet(5, 5, 22, 22),
    ...sheet(73, 5, 22, 22),
    ...sheet(5, 73, 22, 22),
    ...ANALYSIS_CHILDREN.slice(0, 3).map((child) => line([50, 50, 52], [child[0], child[1], child[2] + 2])),
    ...sheet(35, 35, 52, 30),
    // text lines on the home page
    line([40, 42, 54], [58, 42, 54]),
    line([40, 47, 54], [52, 47, 54]),
    line([84, 84, 26], [50, 50, 52]),
    ...sheet(73, 73, 22, 22),
    line([78, 80, 24], [88, 80, 24]),
  ];

  const construct = [
    ...floorGrid(100, 20),
    ...ANALYSIS_CHILDREN.map((child) => polyline([[child[0], child[1], 0], [child[0], child[1], child[2]]])),
    polyline([[50, 50, 0], [50, 50, 52]]),
  ];

  const [homeX, homeY] = project(ANALYSIS_HOME);
  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {/* Scan line runs along y at floor level and travels in +x. */}
      <g className="cc-ill-analysis__scan">
        <path d={polyline([[0, 0, 0], [0, 100, 0]])} />
      </g>
      {ANALYSIS_CHILDREN.map((child, index) => {
        const [cx, cy] = project([child[0], child[1], child[2] + 2]);
        return (
          <circle
            key={index}
            className="cc-ill-analysis__node"
            style={{ "--cc-ill-node": index } as CSSProperties}
            cx={cx}
            cy={cy}
            r={2.6}
          />
        );
      })}
      <circle className="cc-ill__dot" cx={homeX} cy={homeY} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Strategi

const STRATEGY_PATH: Point3[] = [
  [15, 85, 4],
  [15, 50, 4],
  [50, 50, 4],
  [50, 15, 4],
  [85, 15, 4],
];
const STRATEGY_POINTS: Point3[] = [STRATEGY_PATH[0], STRATEGY_PATH[2], STRATEGY_PATH[4]];

export function CcSeoStrategyIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 0, 0, 100, 100, 4),
    ...box(26, 22, 4, 16, 16, 12),
    ...box(62, 28, 4, 22, 14, 8),
    ...box(4, 4, 4, 16, 18, 6),
    ...box(26, 60, 4, 16, 18, 10),
    ...box(60, 60, 4, 24, 24, 15),
    // pads under the three priority points
    ...STRATEGY_POINTS.map((point) => ({ d: plane(point[0] - 6, point[1] - 6, 4, 12, 12) })),
  ];

  const construct = [
    polyline([[50, -6, 4], [50, 106, 4]]),
    polyline([[-6, 50, 4], [106, 50, 4]]),
    polyline([[0, 106, 0], [100, 106, 0]]),
    ...[0, 25, 50, 75, 100].map((x) => polyline([[x, 103, 0], [x, 109, 0]])),
  ];

  const segments = STRATEGY_PATH.slice(1).map((point, index) => polyline([STRATEGY_PATH[index], point]));
  const [endX, endY] = project(STRATEGY_PATH[STRATEGY_PATH.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {segments.map((d, index) => (
        <path
          key={index}
          d={d}
          pathLength={1}
          className="cc-ill-strategy__route"
          style={{ "--cc-ill-segment": index } as CSSProperties}
        />
      ))}
      {STRATEGY_POINTS.map((point, index) => {
        const [cx, cy] = project(point);
        return <circle key={index} className="cc-ill-strategy__point" cx={cx} cy={cy} r={3.4} />;
      })}
      <circle className="cc-ill__dot cc-ill-strategy__marker" cx={endX} cy={endY} r={2.6}>
        <animateMotion
          begin="indefinite"
          dur="1.6s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(STRATEGY_PATH)}
        />
      </circle>
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Genomförande

export function CcSeoExecutionIllustration({ maskId }: { maskId: string | null }) {
  const foundation: Shape[] = [...box(10, 10, 0, 80, 80, 8), ...box(16, 16, 8, 68, 68, 4)];

  const columns: Shape[] = [];
  const columnPositions: [number, number][] = [];
  for (const y of [22, 70]) {
    for (const x of [22, 38, 54, 70]) columnPositions.push([x, y]);
  }
  for (const y of [38, 54]) {
    for (const x of [22, 70]) columnPositions.push([x, y]);
  }
  columnPositions
    .sort((a, b) => a[0] + a[1] - (b[0] + b[1]))
    .forEach(([x, y]) => columns.push(...box(x, y, 12, 6, 6, 30)));

  const roof: Shape[] = [...box(16, 16, 42, 68, 68, 6), line([16, 50, 48], [84, 50, 48]), line([50, 16, 48], [50, 84, 48])];

  const links = [
    polyline([[26, 26, 48], [50, 50, 48], [74, 26, 48]]),
    polyline([[26, 74, 48], [50, 50, 48], [74, 74, 48]]),
  ];

  const construct = [
    polyline([[10, 98, 0], [90, 98, 0]]),
    polyline([[98, 10, 0], [98, 90, 0]]),
    polyline([[90, 90, 0], [90, 90, 60]]),
    polyline([[10, 90, 0], [10, 90, 60]]),
    polyline([[90, 10, 0], [90, 10, 60]]),
  ];

  const [hubX, hubY] = project([50, 50, 48]);
  return (
    <IllustrationFrame
      layers={[
        { className: "cc-ill-execution__foundation", shapes: foundation },
        { className: "cc-ill-execution__columns", shapes: columns },
        { className: "cc-ill-execution__roof", shapes: roof },
      ]}
      construct={construct}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <g className="cc-ill-execution__links">
        {links.map((d, index) => (
          <path key={index} d={d} pathLength={1} style={{ "--cc-ill-segment": index } as CSSProperties} />
        ))}
      </g>
      <circle className="cc-ill__dot" cx={hubX} cy={hubY} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Uppföljning

// Schematic only: it rises and dips, with no values and no promise of constant growth.
const MONITOR_CURVE: Point3[] = [
  [20, 30, 24],
  [30, 30, 32],
  [40, 30, 28],
  [50, 30, 40],
  [60, 30, 35],
  [70, 30, 46],
  [80, 30, 42],
];
const MONITOR_POINTS = [MONITOR_CURVE[1], MONITOR_CURVE[3], MONITOR_CURVE[5]];

export function CcSeoMonitoringIllustration({ maskId }: { maskId: string | null }) {
  const arc: Point3[] = [];
  for (let step = 0; step <= 12; step += 1) {
    const angle = Math.PI + (step / 12) * (Math.PI / 2);
    arc.push([78 + 20 * Math.cos(angle), 88 + 20 * Math.sin(angle), 4]);
  }

  const shapes: Shape[] = [
    ...box(0, 34, 0, 100, 66, 4),
    // instrument quadrant on the base plate
    { d: polyline(arc) },
    line([78, 88, 4], [58, 88, 4]),
    line([78, 88, 4], [78, 68, 4]),
    // drafting board standing on the plate
    ...box(10, 26, 4, 80, 4, 62),
    // axes on the board face (y = 30)
    line([16, 30, 14], [84, 30, 14]),
    line([16, 30, 14], [16, 30, 60]),
    ...[30, 50, 70].map((x) => line([x, 30, 14], [x, 30, 17])),
    { d: polyline(MONITOR_CURVE) },
  ];

  const construct = [
    ...[24, 34, 44, 54].map((z) => polyline([[16, 30, z], [84, 30, z]])),
    ...[1, 2].map((part) => {
      const angle = Math.PI + (part / 3) * (Math.PI / 2);
      return polyline([[78, 88, 4], [78 + 20 * Math.cos(angle), 88 + 20 * Math.sin(angle), 4]]);
    }),
  ];

  const pulse = project(MONITOR_POINTS[2]);
  const [endX, endY] = project(MONITOR_CURVE[MONITOR_CURVE.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-monitoring__curve" d={polyline(MONITOR_CURVE)} pathLength={1} />
      {MONITOR_POINTS.map((point, index) => {
        const [cx, cy] = project(point);
        return <circle key={index} className="cc-ill-monitoring__point" cx={cx} cy={cy} r={2.4} />;
      })}
      <circle className="cc-ill-monitoring__pulse" cx={pulse[0]} cy={pulse[1]} r={2.4} />
      <circle className="cc-ill__dot cc-ill-monitoring__marker" cx={endX} cy={endY} r={2.6}>
        <animateMotion
          begin="indefinite"
          dur="1.4s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(MONITOR_CURVE)}
        />
      </circle>
    </IllustrationFrame>
  );
}

export type ProcessIllustration = (props: { maskId: string | null }) => ReactNode;

export const SEO_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  CcSeoAnalysisIllustration,
  CcSeoStrategyIllustration,
  CcSeoExecutionIllustration,
  CcSeoMonitoringIllustration,
];
